import React, { useState, useEffect, useRef } from "react"
import parse from "html-react-parser"
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  CodeBracketIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import Menu from "aldoo-ra/Menu"

const PREVIEW_MODES = {
  DESKTOP: { name: "desktop", width: 1280 },
}

const PreviewDialog = ({ content, onClose, openHtmlEditor }) => {
  const [previewMode, setPreviewMode] = useState(PREVIEW_MODES.DESKTOP)
  const [hasComplexLayout, setHasComplexLayout] = useState(false)
  const dialogRef = useRef(null)
  const contentRef = useRef(null)

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose])

  // Handle clicking outside dialog
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  // Update content and detect if it has complex layout
  useEffect(() => {
    if (contentRef.current && content) {
      // Parse the HTML to extract styles and body content
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, "text/html")

      // Extract and inject styles from the head
      const styleElements = doc.querySelectorAll("style")

      // Remove existing preview styles
      const existingPreviewStyles = document.querySelectorAll("style[data-preview-styles]")
      existingPreviewStyles.forEach((style) => style.remove())

      // Inject new styles with higher specificity for preview container
      styleElements.forEach((styleEl) => {
        const newStyle = document.createElement("style")
        newStyle.setAttribute("data-preview-styles", "true")
        // Wrap all styles to scope them to the preview container
        const scopedStyles = styleEl.textContent
          .split('}')
          .filter(rule => rule.trim())
          .map(rule => {
            const trimmedRule = rule.trim()
            // Skip @rules, keep them as-is
            if (trimmedRule.startsWith('@')) {
              return trimmedRule + '}'
            }
            // For regular CSS rules, don't add extra scoping - let them apply globally
            return trimmedRule + '}'
          })
          .join('\n')
        newStyle.textContent = scopedStyles
        document.head.appendChild(newStyle)
      })

      // Set the body content
      contentRef.current.innerHTML = doc.body.innerHTML || content

      // Check if content has complex HTML structure that needs full-width rendering
      const hasComplexStructure = content && (
        content.includes('<table') ||
        content.includes('cellpadding') ||
        content.includes('cellspacing') ||
        content.includes('container') ||
        content.includes('max-width:') ||
        content.includes('margin: 0 auto') ||
        (content.match(/<div/g) || []).length > 5
      )
      setHasComplexLayout(hasComplexStructure)
    }

    // Cleanup function to remove styles when component unmounts
    return () => {
      const previewStyles = document.querySelectorAll("style[data-preview-styles]")
      previewStyles.forEach((style) => style.remove())
    }
  }, [content])

  const handleSourceView = () => {
    onClose()
    openHtmlEditor()
  }

  const getPreviewTitle = () => {
    if (previewMode.name === "desktop") {
      return "Desktop Preview (Full Width)"
    }
    return `${previewMode.label} (${previewMode.width}x${previewMode.height})`
  }

  const render = (item) => {
    return (
      <div className="flex flex-row justify-between items-center gap-2 w-full p-2 hover:bg-admin_neutral_1/50">
        <div className="flex items-center gap-2">
          {item.icon}
          <span>{item.label}</span>
        </div>
        {item.options?.device && (
          <span className="text-sm text-admin_neutral_2">
            {item.options.device.width}x{item.options.device.height}
          </span>
        )}
      </div>
    )
  }

  const menuData = {
    label: "Mobile Devices",
    render,
    icon: <DevicePhoneMobileIcon className="w-5 h-5" />,
    items: [
      {
        label: "Phones",
        render,
        items: [
          {
            label: "iPhone 14",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPhone 14", width: 390, height: 844 },
            },
          },
          {
            label: "iPhone 14 Pro Max",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPhone 14 Pro Max", width: 430, height: 932 },
            },
          },
          {
            label: "Pixel 7",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Pixel 7", width: 412, height: 915 },
            },
          },
          {
            label: "Samsung S23",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Samsung S23", width: 360, height: 780 },
            },
          },
        ],
      },
      {
        label: "Tablets",
        render,
        items: [
          {
            label: "iPad",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPad", width: 820, height: 1180 },
            },
          },
          {
            label: "iPad Pro",
            render,
            options: {
              action: "selectDevice",
              device: { label: "iPad Pro", width: 1024, height: 1366 },
            },
          },
          {
            label: "Samsung Tab S8",
            render,
            options: {
              action: "selectDevice",
              device: { label: "Samsung Tab S8", width: 1440, height: 900 },
            },
          },
        ],
      },
    ],
  }

  const actionMap = {
    selectDevice: ({ options }) => {
      setPreviewMode({ name: "mobile", ...options.device })
    },
  }

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center backdrop-blur-sm"
      style={{ zIndex: 1100 }}
    >
      <div
        ref={dialogRef}
        className="bg-gray-900 w-full h-full flex flex-col text-white"
      >
        {/* Toolbar */}
        <div className="w-full bg-black bg-opacity-70 p-4 flex items-center justify-between">
          {/* Left side - empty to maintain centering */}
          <div className="w-[156px]"></div>

          {/* Center - Title */}
          <h2 className="text-lg font-medium text-white">
            {getPreviewTitle()}
          </h2>

          {/* Right side - buttons */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setPreviewMode(PREVIEW_MODES.DESKTOP)}
              className={`p-2 rounded-full transition-colors duration-200 ${
                previewMode.name === "desktop"
                  ? "bg-admin_neutral_3 text-white"
                  : "text-white hover:bg-admin_neutral_3"
              }`}
              title="Desktop view"
            >
              <ComputerDesktopIcon className="w-5 h-5" />
            </button>

            <div className="relative">
              <Menu
                menuData={menuData}
                actionMap={actionMap}
                renderAs="vlist.menu"
                itemWidth="250px"
                submenuMode="vertical"
                menuAlign="right"
                rootItemRender={(item) => (
                  <div className="flex flex-row justify-between items-center gap-2 w-full p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white">
                    <div className="flex items-center gap-2">{item.icon}</div>
                  </div>
                )}
                menuItemRender={(item) => (
                  <div className="flex flex-row justify-between items-center gap-2 w-full p-2 hover:bg-admin_neutral_1/50">
                    <div className="flex items-center gap-2">
                      {item.icon}
                      <span>{item.label}</span>
                    </div>
                    {item.options?.device && (
                      <span className="mr-5 text-sm text-admin_neutral_2">
                        {item.options.device.width}x{item.options.device.height}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            {openHtmlEditor && (
              <button
                onClick={handleSourceView}
                className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
                title="View source"
              >
                <CodeBracketIcon className="w-5 h-5" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-admin_neutral_3 transition-colors duration-200 text-white"
              title="Close preview"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto bg-gray-800 p-4">
          <div className="flex justify-center min-h-full">
            <div
              style={{
                width:
                  previewMode.name === "desktop"
                    ? "100%"
                    : `${previewMode.width}px`,
                maxWidth:
                  previewMode.name === "desktop"
                    ? "72rem"
                    : `${previewMode.width}px`,
                height:
                  previewMode.name === "mobile"
                    ? `${previewMode.height}px`
                    : "auto",
              }}
              className="bg-white shadow-xl rounded-lg transition-all duration-300 h-full"
            >
              <div
                ref={contentRef}
                className={`overflow-auto h-full ${hasComplexLayout ? 'text-gray-900' : 'p-6 text-admin_text'}`}
                style={{ color: hasComplexLayout ? '#111827' : undefined }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PreviewDialog
